import React from 'react';
import { LegalPage } from '../components/legal/LegalPage';
import { Seo } from '../components/Seo';

/**
 * LACloud Terms of Service. Structure and boilerplate mirror the vetted Gridzu
 * terms (same operator — Chrono Travel LLC — same Oregon governing law and
 * arbitration approach), adapted for LACloud's LAN file-sharing + backup model,
 * Free/Pro tiers, and Stripe billing.
 */
export const Terms: React.FC = () => (
  <>
    <Seo
      title="Terms of Service — Local Area Cloud"
      description="The terms governing use of Local Area Cloud, the private device-to-device file sharing and backup app by Chrono Travel LLC."
      path="/terms"
    />
  <LegalPage kicker="Legal" title="Terms of Service" effectiveDate="July 8, 2026">
    <div className="l-note">
      <strong>The short version.</strong> LACloud lets your own devices share and back up files
      directly over your local network. Use it lawfully, keep your account secure, and understand
      that the Service is provided "as is." Backups are a convenience, not a guarantee — always keep
      an independent copy of anything important.
    </div>

    <p>
      These Terms of Service ("Terms") are a binding agreement between you and{' '}
      <strong>Chrono Travel LLC</strong> ("Chrono Travel," "we," "us," or "our"), the operator of
      the LACloud service, the localareacloud.com website, and the LACloud desktop and mobile
      applications (together, the "Service"). By creating an account, installing, or using the
      Service, you agree to these Terms and to our{' '}
      <a href="/privacy">Privacy Policy</a>. If you do not agree, do not use the Service.
    </p>

    <h2>1. Eligibility</h2>
    <p>
      You must be at least 13 years old (or the minimum age of digital consent in your jurisdiction)
      and able to form a binding contract. If you use the Service on behalf of an organization, you
      represent that you are authorized to accept these Terms for it.
    </p>

    <h2>2. Accounts</h2>
    <p>
      You need an account to use most features. You agree to provide accurate information, to keep
      your credentials confidential, and to be responsible for all activity under your account.
      Notify us promptly at <a href="mailto:privacy@localareacloud.com">privacy@localareacloud.com</a>{' '}
      if you suspect unauthorized use. You may sign in with an email and password or with a
      third-party provider (Google or Microsoft); your use of those sign-in options is also subject
      to that provider's terms.
    </p>

    <h2>3. License and restrictions</h2>
    <p>
      Subject to these Terms, we grant you a limited, personal, revocable, non-exclusive,
      non-transferable license to install and use the LACloud applications for your own lawful use.
      You may not:
    </p>
    <ul>
      <li>reverse engineer, decompile, or disassemble the Service except to the extent this restriction is prohibited by law;</li>
      <li>circumvent or interfere with security, licensing, device limits, or access controls;</li>
      <li>remove or alter any proprietary notices;</li>
      <li>resell, sublicense, or use the Service to build a competing product;</li>
      <li>use automated means to access the Service in a way that burdens our infrastructure; or</li>
      <li>use the Service to violate any law or the rights of others.</li>
    </ul>

    <h2>4. Your data and content</h2>
    <p>
      You retain all rights to the files and content you transfer or back up using the Service ("Your
      Data"). LACloud transfers Your Data <strong>directly between your devices over your local
      network</strong>; we do not store, access, or transmit the contents of Your Data on our
      servers. You are solely responsible for Your Data and for having the rights necessary to store
      and transfer it. Our servers store only account and device-registry information as described in
      the <a href="/privacy">Privacy Policy</a>.
    </p>

    <h2>5. Privacy</h2>
    <p>
      Our <a href="/privacy">Privacy Policy</a> explains what limited information we collect and how
      we handle it. By using the Service you agree to that Policy.
    </p>

    <h2>6. Third-party services and components</h2>
    <p>
      The Service relies on third-party services, including identity providers (Google, Microsoft),
      our payment processor (Stripe), and hosting/infrastructure providers. Your use of those
      services is governed by their own terms and policies, and we are not responsible for them. For
      cross-network ("Pro") features, you may connect your own VPN infrastructure; you are
      responsible for configuring and securing it.
    </p>

    <h2>7. Updates and changes to the Service</h2>
    <p>
      We may update, add, or remove features, and we may deliver automatic updates to the
      applications to keep them secure and functional. We may modify or discontinue parts of the
      Service, and will use reasonable efforts to give notice of material changes.
    </p>

    <h2>8. Fees, plans, and billing</h2>
    <p>
      LACloud offers a <strong>free tier</strong> (currently up to two devices per account) and a
      paid <strong>"Pro" tier</strong> (unlimited devices and cross-network reach). Paid plans are
      billed through Stripe in advance on the interval you select (for example, annual or multi-year).
    </p>
    <ul>
      <li><strong>Auto-renewal.</strong> Subscriptions renew automatically at the end of each term at the then-current price unless you cancel before the renewal date.</li>
      <li><strong>Cancellation.</strong> You may cancel at any time; cancellation takes effect at the end of the current paid term, and you keep Pro features until then.</li>
      <li><strong>Refunds.</strong> Except where required by law, fees are non-refundable. We may, at our discretion, offer a refund for a recent charge — contact us to ask.</li>
      <li><strong>Taxes and changes.</strong> You are responsible for applicable taxes. We may change prices, with notice that applies to future terms.</li>
      <li><strong>Downgrades.</strong> If a paid plan ends and your account exceeds the free device limit, some devices may lose access until you remove devices or renew.</li>
    </ul>

    <h2>9. Intellectual property</h2>
    <p>
      The Service, including its software, design, branding, and content (excluding Your Data), is
      owned by Chrono Travel LLC and its licensors and is protected by intellectual property laws.
      These Terms grant you no rights in our trademarks or brand without our prior written permission.
    </p>

    <h2>10. Feedback</h2>
    <p>
      If you send us feedback, suggestions, or ideas, you grant us a perpetual, irrevocable,
      royalty-free license to use them for any purpose without obligation to you.
    </p>

    <h2>11. Disclaimer of warranties</h2>
    <p>
      The Service is provided <strong>"as is" and "as available," without warranty of any kind</strong>,
      whether express, implied, or statutory, including implied warranties of merchantability, fitness
      for a particular purpose, title, and non-infringement. We do not warrant that the Service will
      be uninterrupted, error-free, or secure, or that any backup, transfer, or synchronization will
      be complete or error-free. <strong>You are responsible for maintaining independent backups of
      important data.</strong>
    </p>

    <h2>12. Limitation of liability</h2>
    <p>
      To the maximum extent permitted by law, Chrono Travel LLC and its owners, employees, and
      suppliers will not be liable for any indirect, incidental, special, consequential, exemplary,
      or punitive damages, or for any loss of data, files, profits, goodwill, or business, arising
      out of or relating to the Service. Our total liability for all claims relating to the Service
      will not exceed the greater of (a) the amounts you paid us for the Service in the twelve months
      before the claim, or (b) one hundred U.S. dollars (US $100). Some jurisdictions do not allow
      certain limitations, so some of the above may not apply to you.
    </p>

    <h2>13. Indemnification</h2>
    <p>
      You agree to indemnify and hold harmless Chrono Travel LLC from any claims, damages, losses,
      and expenses (including reasonable legal fees) arising out of your use of the Service, Your
      Data, or your violation of these Terms or of any law or third-party rights.
    </p>

    <h2>14. Dispute resolution, binding arbitration, and class-action waiver</h2>
    <p>
      <strong>Please read this section carefully — it affects your legal rights.</strong> Except for
      claims that qualify for small-claims court and requests for injunctive relief, any dispute
      arising out of or relating to these Terms or the Service will be resolved by{' '}
      <strong>final and binding arbitration on an individual basis</strong>, administered under the
      U.S. Federal Arbitration Act. <strong>You and Chrono Travel LLC waive any right to a jury trial
      and to participate in a class, collective, or representative action.</strong>
    </p>
    <p>
      <strong>Opt-out.</strong> You may opt out of this arbitration agreement by emailing{' '}
      <a href="mailto:privacy@localareacloud.com">privacy@localareacloud.com</a> within 30 days of
      first accepting these Terms, stating your name and that you opt out of arbitration.
    </p>

    <h2>15. Governing law and venue</h2>
    <p>
      These Terms are governed by the laws of the <strong>State of Oregon</strong>, United States,
      without regard to its conflict-of-laws rules. Subject to Section 14, you agree to the exclusive
      jurisdiction and venue of the state and federal courts located in the State of Oregon.
    </p>

    <h2>16. Term and termination</h2>
    <p>
      These Terms apply while you use the Service. You may stop using the Service and delete your
      account at any time. We may suspend or terminate your access if you violate these Terms or to
      protect the Service or other users. Provisions that by their nature should survive termination
      (including Sections 9, 11–15) will survive.
    </p>

    <h2>17. Export controls and compliance</h2>
    <p>
      You agree to comply with all applicable export-control and sanctions laws and represent that you
      are not located in, or a resident of, an embargoed jurisdiction, and are not on any restricted-
      party list.
    </p>

    <h2>18. Changes to these Terms</h2>
    <p>
      We may update these Terms from time to time. When we make material changes, we will update the
      "Effective" date above and, where appropriate, provide notice. Your continued use of the Service
      after an update means you accept the revised Terms.
    </p>

    <h2>19. Miscellaneous</h2>
    <p>
      These Terms and the Privacy Policy are the entire agreement between you and Chrono Travel LLC
      regarding the Service. If any provision is found unenforceable, the rest remain in effect. Our
      failure to enforce a provision is not a waiver. You may not assign these Terms without our
      consent; we may assign them in connection with a merger, acquisition, or sale of assets.
    </p>

    <h2>20. Contact</h2>
    <p>
      Questions about these Terms? Contact Chrono Travel LLC at{' '}
      <a href="mailto:privacy@localareacloud.com">privacy@localareacloud.com</a>.
    </p>
  </LegalPage>
  </>
);

export default Terms;
